
import { useState } from "react";
import { TaskCard } from "./TaskCard";
import { TaskDetailsDialog } from "./TaskDetailsDialog";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { addDays, isPast, parseISO } from "date-fns";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TaskListViewProps {
  tasks: any[];
  userRole?: string | null;
  onTaskComplete: (taskId: string) => Promise<void>;
  onStatusChange?: (taskId: string, newStatus: string) => Promise<void>;
  showOldCompletedTasks?: boolean;
}

export const TaskListView = ({ 
  tasks, 
  userRole, 
  onTaskComplete,
  onStatusChange,
  showOldCompletedTasks = false
}: TaskListViewProps) => {
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    todo: true,
    in_progress: true,
    on_hold: true,
    completed: false
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const statusSections = [
    { id: 'todo', label: 'To Do' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'on_hold', label: 'On Hold' },
    { id: 'completed', label: 'Completed' }
  ];

  const handleDelete = async () => {
    if (!taskToDelete?.id || isDeleting || !currentOrganization) return;

    try {
      setIsDeleting(true);
      setShowDeleteConfirm(false);

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "delete",
          taskId: taskToDelete.id,
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setTaskToDelete(null);
    }
  };

  const handleTaskClick = (task: any) => {
    console.log("TaskListView: Task clicked!", { task: task.title, taskId: task.id });
    console.log("TaskListView: Setting selected task and opening dialog");
    
    setSelectedTask(task);
    setIsDetailsOpen(true);
  };

  const handleDetailsDialogChange = (open: boolean) => {
    console.log("TaskListView: Details dialog state changed to:", open);
    setIsDetailsOpen(open);
    if (!open) {
      console.log("TaskListView: Clearing selected task");
      setSelectedTask(null);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const filterOldCompletedTasks = (task: any) => {
    if (task.status !== 'completed' || showOldCompletedTasks) {
      return true;
    }
    
    if (!task.updated_at) return true;
    
    const updatedDate = parseISO(task.updated_at);
    const oneWeekAgo = addDays(new Date(), -7);
    
    return updatedDate > oneWeekAgo;
  };

  const filteredTasks = tasks.filter(filterOldCompletedTasks);

  console.log("TaskListView: Rendering with", filteredTasks.length, "tasks");

  return (
    <div className="space-y-6">
      {statusSections.map(section => {
        const sectionTasks = filteredTasks.filter(task => task.status === section.id);
        if (sectionTasks.length === 0) return null;
        
        return (
          <Collapsible 
            key={section.id} 
            open={expandedSections[section.id]} 
            onOpenChange={() => toggleSection(section.id)}
            className="border rounded-md overflow-hidden"
          >
            <div className="flex items-center justify-between bg-muted/50 px-4 py-2">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="p-0 hover:bg-transparent">
                  <h3 className="text-lg font-medium flex items-center">
                    {expandedSections[section.id] ? (
                      <ChevronUp className="mr-2 h-4 w-4" />
                    ) : (
                      <ChevronDown className="mr-2 h-4 w-4" />
                    )}
                    {section.label}
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({sectionTasks.length})
                    </span>
                  </h3>
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectionTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onTaskClick={handleTaskClick}
                    onDeleteClick={(task) => {
                      console.log("TaskListView: Delete clicked for task:", task.title);
                      setTaskToDelete(task);
                      setShowDeleteConfirm(true);
                    }}
                    onTaskComplete={onTaskComplete}
                    onStatusChange={onStatusChange}
                    userRole={userRole}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}

      <TaskDetailsDialog
        task={selectedTask}
        open={isDetailsOpen}
        onOpenChange={handleDetailsDialogChange}
        userRole={userRole}
        onUpdate={() => {
          console.log("TaskListView: Task updated, invalidating queries");
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
        }}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
      />
    </div>
  );
};
